// ./src/components/features/Weather/WeatherPanel.tsx
//
// THE ONE WEATHER RENDERER — hourly strip, daily outlook, air quality and pollen.
//
// EXTRACTED FROM `eventDashboardWidgets.tsx` (2026-09-25) rather than copied, when Home needed the
// same forecast. It was ~750 lines of weather living inside one page file; a second copy would have
// been two products worth of condition-code mapping drifting apart. Nothing about it was ever
// event-specific - it takes a time range, a timezone and a forecast.
//
// It is PRESENTATIONAL. The forecast is fetched by the caller (and cached server-side in
// `cache_entry` since migration 1830 - at most one provider call per location per hour).

import React, { useMemo } from 'react';
import { Badge, Box, Card, Divider, Group, Loader, ScrollArea, Stack, Text, ThemeIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { AppIcon } from '@/components/generic';
import { AirQuality, Pollen, WeatherForecast, WeatherForecastDay, WeatherForecastHour } from '@/utils/api';

import { EVENT_FORECAST_DAYS } from './eventWeatherWindow';

dayjs.extend(utc);
dayjs.extend(timezone);

// ── Weather forecast (beside the address in the hero) ─────────────────────────

// Map a Google condition code to a bundled icon + accent color.
const weatherIcon = (code?: string | null): { icon: string; color: string } => {
  const c = (code ?? '').toLowerCase();
  if (c.includes('thunder') || c.includes('storm')) return { icon: 'CloudStorm', color: 'grape' };
  if (c.includes('snow') || c.includes('blizzard') || c.includes('flurr') || c.includes('sleet') || c.includes('hail'))
    return { icon: 'CloudSnow', color: 'blue' };
  if (c.includes('rain') || c.includes('shower') || c.includes('drizzle')) return { icon: 'CloudRain', color: 'blue' };
  if (c.includes('fog') || c.includes('haze') || c.includes('mist')) return { icon: 'CloudFog', color: 'gray' };
  if (c.includes('wind')) return { icon: 'Wind', color: 'gray' };
  if (c.includes('cloud')) return { icon: 'Cloud', color: 'gray' };
  if (c.includes('clear') || c.includes('sun')) return { icon: 'Sun', color: 'yellow' };
  return { icon: 'Cloud', color: 'gray' };
};

// A big translucent emoji sits behind the widget as a mood cue.
const conditionEmoji = (code?: string | null): string => {
  const c = (code ?? '').toLowerCase();
  if (c.includes('thunder') || c.includes('storm')) return '⛈️';
  if (c.includes('snow') || c.includes('blizzard') || c.includes('flurr') || c.includes('sleet') || c.includes('hail')) return '❄️';
  if (c.includes('rain') || c.includes('shower') || c.includes('drizzle')) return '🌧️';
  if (c.includes('fog') || c.includes('haze') || c.includes('mist')) return '🌫️';
  if (c.includes('wind')) return '💨';
  if (c.includes('cloud')) return '☁️';
  if (c.includes('clear') || c.includes('sun')) return '☀️';
  return '🌤️';
};

const BAD_CODES = ['rain', 'snow', 'thunder', 'storm', 'sleet', 'hail', 'shower', 'drizzle', 'blizzard', 'flurr'];
const isRoughHour = (h: WeatherForecastHour): boolean => {
  const c = (h.conditionCode ?? '').toLowerCase();
  return BAD_CODES.some((b) => c.includes(b)) || (h.precipProbabilityPct ?? 0) >= 50;
};
const tempLabel = (v?: number | null): string => (v == null ? '—' : `${Math.round(v)}°`);
const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Text color by warmth so a glance reads hot/cold, not just numbers.
const tempColorFor = (c?: number | null): string | undefined => {
  if (c == null) return undefined;
  if (c <= 4) return 'blue.7';
  if (c <= 12) return 'cyan.7';
  if (c <= 22) return undefined; // comfortable → default ink
  if (c <= 29) return 'orange.7';
  return 'red.7';
};
// Subtle per-hour background for a rough hour so the problem window pops.
const roughHourTint = (h: WeatherForecastHour): string | undefined => {
  if (!isRoughHour(h)) return undefined;
  const c = (h.conditionCode ?? '').toLowerCase();
  if (c.includes('thunder') || c.includes('storm')) return 'var(--mantine-color-red-1)';
  if (c.includes('snow') || c.includes('sleet') || c.includes('hail')) return 'var(--mantine-color-blue-1)';
  return 'var(--mantine-color-yellow-1)';
};
// Hour label in the EVENT's timezone (so a glance matches the venue's local time).
const hourLabel = (iso?: string | null, tz?: string | null): string => {
  if (!iso) return '';
  const d = dayjs(iso);
  return (tz ? d.tz(tz) : d).format('h A');
};
/**
 * The forecast day that is the EVENT's (YYYY-MM-DD on its clock): its first day — or, once it is under way, TODAY (a
 * multi-day event's first day is no longer in the forecast, and its sunset / pollen would otherwise vanish mid-event).
 */
const eventDayKey = (startsAt: string, tz: string): string => {
  const first = dayjs.utc(startsAt).tz(tz).format('YYYY-MM-DD');
  const today = dayjs().tz(tz).format('YYYY-MM-DD');

  return first > today ? first : today;
};

// Precise time (with minutes) — for sunrise/sunset.
const timeLabel = (iso?: string | null, tz?: string | null): string => {
  if (!iso) return '';
  const d = dayjs(iso);
  return (tz ? d.tz(tz) : d).format('h:mm A');
};

// Rank an air-quality category by severity (works across Google's Universal +
// local index category labels), so we can color it and detect worsening.
const aqiRank = (category?: string | null): number => {
  const c = (category ?? '').toLowerCase();
  if (c.includes('hazardous')) return 5;
  if (c.includes('very')) return 4;
  if (c.includes('unhealthy') || c.includes('poor')) return 3;
  if (c.includes('sensitive') || c.includes('low')) return 2;
  if (c.includes('moderate') || c.includes('fair')) return 1;
  return 0; // good / excellent / clean
};
const AQI_COLORS = ['green', 'lime', 'orange', 'red', 'grape', 'dark'];
const aqiColor = (rank: number): string => AQI_COLORS[rank] ?? 'gray';

// Pollen index is 0–5; advise at moderate (3) and up.
const POLLEN_COLORS = ['green', 'green', 'yellow', 'orange', 'red', 'grape'];
const pollenColor = (index: number): string => POLLEN_COLORS[index] ?? 'gray';
const POLLEN_ADVISORY = 3;

// Part of day (in the event's tz) drives the emoji + gradient tone.
type PartOfDay = 'morning' | 'day' | 'evening' | 'night';
const partOfDay = (hour: number): PartOfDay => {
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 17) return 'day';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};
const isClearCode = (code?: string | null): boolean => {
  const c = (code ?? '').toLowerCase();
  return c.includes('clear') || c.includes('sun');
};
// Clear sky reads very differently by time of day; other conditions don't.
const moodEmojiFor = (code: string | null | undefined, part: PartOfDay): string => {
  if (isClearCode(code)) {
    if (part === 'night') return '🌙';
    if (part === 'morning') return '🌅';
    if (part === 'evening') return '🌆';
    return '☀️';
  }
  return conditionEmoji(code);
};

// Advisory thresholds (metric).
const WIND_KMH_ADVISORY = 38; // gusty enough to trouble tents / décor
const FEELS_HOT_C = 32;
const FEELS_COLD_C = 0;
const UV_ADVISORY = 6; // WHO "high" and above
const uvLabel = (uv: number): string => (uv >= 11 ? 'Extreme' : uv >= 8 ? 'Very high' : 'High');

interface WeatherWarning {
  icon: string;
  color: string;
  /** Full sentence for the widget's warning band (incl. event-start context). */
  text: string;
  /** Terse version for the hero chip. */
  shortText: string;
}

type WeatherView =
  | { mode: 'none' }
  | { mode: 'daily'; day: WeatherForecastDay }
  | {
      mode: 'hourly';
      hours: WeatherForecastHour[];
      multiDay: boolean;
      /** Prioritized advisories (precip → wind → heat/cold). First drives the chip. */
      warnings: WeatherWarning[];
      /** Window the columns cover — the panel turns this into a date label. */
      rangeStart?: string | null;
      rangeEnd?: string | null;
      /** True when the event is already under way, so the window starts at "now". */
      ongoing: boolean;
    };

// Collect the notable advisories across the forecast window, most important first.
const buildAdvisories = (
  slice: WeatherForecastHour[],
  tz: string | null | undefined,
  startsAt: string | null | undefined,
  start: dayjs.Dayjs,
  ongoing: boolean,
): WeatherWarning[] => {
  const out: WeatherWarning[] = [];

  // 1) Rain / snow / storms.
  const rough = slice.find(isRoughHour);
  if (rough) {
    const w = weatherIcon(rough.conditionCode);
    const stormy = (rough.conditionCode ?? '').toLowerCase().match(/thunder|storm/);
    const cond = cap(rough.description || rough.conditionCode || 'Rough weather');
    const at = hourLabel(rough.time, tz);
    const pct = rough.precipProbabilityPct != null ? ` · ${rough.precipProbabilityPct}%` : '';
    const startAt = startsAt ? hourLabel(startsAt, tz) : '';
    const roughAfterStart = rough.time ? !dayjs(rough.time).isBefore(start) : true;
    const startNote = startAt && !ongoing && roughAfterStart ? ` · event starts ${startAt}` : '';
    out.push({
      icon: w.icon,
      color: stormy ? 'red' : 'yellow',
      text: `${cond} expected around ${at}${pct}${startNote}`,
      shortText: `${cond} ~${at}${pct}`,
    });
  }

  // 2) Gusty wind.
  const windy = slice.reduce<WeatherForecastHour | null>(
    (m, h) => ((h.windSpeedKmh ?? 0) > (m?.windSpeedKmh ?? 0) ? h : m),
    null,
  );
  if (windy && (windy.windSpeedKmh ?? 0) >= WIND_KMH_ADVISORY) {
    const kmh = Math.round(windy.windSpeedKmh as number);
    out.push({
      icon: 'Wind',
      color: 'gray',
      text: `Gusty winds ~${kmh} km/h around ${hourLabel(windy.time, tz)} — secure décor / tents`,
      shortText: `Wind ~${kmh} km/h`,
    });
  }

  // 3) Feels-like extremes.
  const feels = slice.map((h) => h.feelsLikeC).filter((v): v is number => v != null);
  if (feels.length) {
    const hot = Math.max(...feels);
    const cold = Math.min(...feels);
    if (hot >= FEELS_HOT_C) {
      out.push({
        icon: 'Flame',
        color: 'orange',
        text: `Feels like ${Math.round(hot)}° — heat, plan water & shade`,
        shortText: `Feels ${Math.round(hot)}° · heat`,
      });
    } else if (cold <= FEELS_COLD_C) {
      out.push({
        icon: 'Snowflake',
        color: 'blue',
        text: `Feels like ${Math.round(cold)}° — cold, plan warmth`,
        shortText: `Feels ${Math.round(cold)}° · cold`,
      });
    }
  }

  // 4) High UV (daytime only).
  const uvPeak = slice
    .filter((h) => h.isDaytime !== false && h.uvIndex != null)
    .reduce<WeatherForecastHour | null>((m, h) => ((h.uvIndex ?? 0) > (m?.uvIndex ?? 0) ? h : m), null);
  if (uvPeak && (uvPeak.uvIndex ?? 0) >= UV_ADVISORY) {
    const uv = Math.round(uvPeak.uvIndex as number);
    out.push({
      icon: 'Sun',
      color: 'yellow',
      text: `${uvLabel(uv)} UV (${uv}) around ${hourLabel(uvPeak.time, tz)} — sunscreen & shade`,
      shortText: `UV ${uv} · ${uvLabel(uv).toLowerCase()}`,
    });
  }

  return out;
};

const buildWeatherView = (
  startsAt?: string | null,
  endsAt?: string | null,
  forecast?: WeatherForecast | null,
  tz?: string | null,
): WeatherView => {
  if (!forecast || (!forecast.hours?.length && !forecast.days?.length)) return { mode: 'none' };
  if (!startsAt) return { mode: 'none' };

  const now = dayjs();
  const start = dayjs(startsAt);
  const end = endsAt ? dayjs(endsAt) : start.add(2, 'hour');
  const multiDay = end.diff(start, 'hour') >= 24;
  const ongoing = now.isAfter(start);
  const anchor = ongoing ? now : start; // ongoing → from now
  const windowEnd = multiDay ? anchor.add(24, 'hour') : end;

  const hours = forecast.hours ?? [];
  const slice = hours
    .filter((h) => {
      if (!h.time) return false;
      const th = dayjs(h.time);
      return !th.isBefore(anchor.startOf('hour')) && !th.isAfter(windowEnd.add(30, 'minute'));
    })
    .slice(0, 12);

  if (slice.length > 0) {
    return {
      mode: 'hourly',
      hours: slice,
      multiDay,
      warnings: buildAdvisories(slice, tz, startsAt, start, ongoing),
      rangeStart: slice[0]?.time,
      rangeEnd: slice[slice.length - 1]?.time,
      ongoing,
    };
  }

  return { mode: 'none' };
};

// A one-line weather summary for the hero's key-facts (the full forecast lives
// in the grid's Weather widget). Prefers the hourly window around the event, else
// the event's own day — null when the forecast has neither. `warn` is set when rain/snow/storms are likely.
interface WeatherChipData {
  icon: string;
  color: string;
  label: string;
  warn: boolean;
  /** Terse warning (e.g. "Light rain ~3 PM · 60%") when weather turns rough. */
  warnText: string | null;
}
export const buildWeatherChip = (
  startsAt?: string | null,
  endsAt?: string | null,
  forecast?: WeatherForecast | null,
  tz?: string | null,
): WeatherChipData | null => {
  const view = buildWeatherView(startsAt, endsAt, forecast, tz);
  if (view.mode === 'hourly') {
    const h0 = view.hours[0];
    const maxPrecip = Math.max(0, ...view.hours.map((h) => h.precipProbabilityPct ?? 0));
    const wi = weatherIcon(h0?.conditionCode);
    const parts = [tempLabel(h0?.tempC)];
    const desc = h0?.description || h0?.conditionCode;
    if (desc) parts.push(desc);
    if (maxPrecip > 0) parts.push(`${maxPrecip}%`);
    const primary = view.warnings[0] ?? null;
    return {
      icon: primary?.icon ?? wi.icon,
      color: primary?.color ?? wi.color,
      label: parts.join(' · '),
      warn: !!primary,
      warnText: primary?.shortText ?? null,
    };
  }

  if (forecast?.days?.length && startsAt) {
    const key = eventDayKey(startsAt, tz || dayjs.tz.guess());
    // ⚠ The EVENT's day or nothing (#182). This fell back to `days[0]` — TODAY — so a far event's top card showed
    // today's weather under its own date, with nothing to say it was not the event's.
    const day = forecast.days.find((d) => d.date === key);
    if (day) {
      const wi = weatherIcon(day.conditionCode);
      const parts = [`${tempLabel(day.tempMaxC)}${day.tempMinC != null ? ` / ${tempLabel(day.tempMinC)}` : ''}`];
      const desc = day.description || day.conditionCode;
      if (desc) parts.push(desc);
      if ((day.precipProbabilityPct ?? 0) > 0) parts.push(`${day.precipProbabilityPct}%`);
      const rough = BAD_CODES.some((b) => (day.conditionCode ?? '').toLowerCase().includes(b)) || (day.precipProbabilityPct ?? 0) >= 50;
      const warnText = rough
        ? `${cap(day.description || day.conditionCode || 'Rough weather')}${(day.precipProbabilityPct ?? 0) > 0 ? ` · ${day.precipProbabilityPct}%` : ''}`
        : null;
      return { icon: wi.icon, color: rough ? 'yellow' : wi.color, label: parts.join(' · '), warn: rough, warnText };
    }
  }

  return null;
};

// The following days, as a strip — Home hands it to the panel as its footer (Home's "the next few days").
// ⚠⚠ It draws NO card of its own. It shipped on Home as a second bordered card titled "Weather" under the panel —
// the same word twice, stacked, about one place. And the event dashboard no longer shows it at all (#182): for an
// event beyond the forecast it was "the next N days", which nobody read as "not your event's weather".
export const WeatherMultiDayStrip: React.FC<{
  days: WeatherForecastDay[];
  caption: string;
}> = ({ days, caption }) => {
  return (
    <>
      <Text size="sm" c="dimmed" mb="sm">
        {caption}
      </Text>
      <ScrollArea type="scroll" offsetScrollbars>
        <Group gap="sm" wrap="nowrap" align="flex-start">
          {days.map((d) => {
            const wi = weatherIcon(d.conditionCode);
            return (
              <Stack key={d.date ?? Math.random()} gap={4} align="center" style={{ minWidth: 56 }}>
                <Text size="sm" c="dimmed">
                  {d.date ? dayjs(d.date).format('ddd D') : '—'}
                </Text>
                <AppIcon iconName={wi.icon} size={22} color={`var(--mantine-color-${wi.color}-6)`} />
                <Group gap={4} wrap="nowrap">
                  <Text size="md" fw={600} c={tempColorFor(d.tempMaxC)}>
                    {tempLabel(d.tempMaxC)}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {tempLabel(d.tempMinC)}
                  </Text>
                </Group>
                {(d.precipProbabilityPct ?? 0) > 0 && (
                  <Text size="md" c="blue">
                    {Math.round(d.precipProbabilityPct as number)}%
                  </Text>
                )}
              </Stack>
            );
          })}
        </Group>
      </ScrollArea>
    </>
  );
};

// Compact forecast block beside the address — a glance tells you whether to
// change plans. Shows hourly around the event (or the next 24h for a long/multi-
// day event) with a heads-up when rain/snow/storms move in; otherwise the daily
// outlook for the event's date. Live data via useEventWeather.
export const WeatherPanel: React.FC<{
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  forecast: WeatherForecast | null;
  loading: boolean;
  airQuality?: AirQuality | null;
  pollen?: Pollen | null;
  locationLabel?: string | null;
  /**
   * Extra content INSIDE this card, below the forecast.
   *
   * ⚠ It exists so a caller can extend the forecast without adding a SECOND card titled "Weather" —
   * which is exactly how Home shipped it the first time. One place, one card.
   */
  footer?: React.ReactNode;
  /**
   * The event is further out than the forecast reaches (`eventWeatherWindow` → FAR), so no forecast was asked for:
   * the card says when one will appear, and shows none (#182).
   */
  farOut?: boolean;
}> = ({ startsAt, endsAt, timezone, forecast, loading, airQuality, pollen, locationLabel, footer, farOut }) => {
  const { t } = useTranslation(['events']);

  // Prefer hourly around the event; else the event's own day — never another day's.
  const tz = timezone || dayjs.tz.guess();
  const view = useMemo<WeatherView>(() => {
    const hourly = buildWeatherView(startsAt, endsAt, forecast, tz);
    if (hourly.mode !== 'none') return hourly;
    if (!forecast?.days?.length || !startsAt) return { mode: 'none' };
    const key = eventDayKey(startsAt, tz);
    const day = forecast.days.find((d) => d.date === key);
    return day ? { mode: 'daily', day } : { mode: 'none' };
  }, [startsAt, endsAt, tz, forecast]);

  // ⚠⚠ BEYOND THE FORECAST = NO FORECAST (#182). This used to swap the whole card for a plain "N-day outlook" strip of
  // the coming days: nobody read it as "not your event's weather", and the swap was the owner's flash — the gradient
  // card drew while loading, then the plain strip replaced it. Now the SAME card stays and says when the forecast
  // appears. The dashboard does not even ask for one (`farOut`); a forecast that stops short of the event lands here too.
  const fDays = forecast?.days ?? [];
  const eventKey = startsAt ? eventDayKey(startsAt, tz) : null;
  const lastKey = fDays.length ? fDays[fDays.length - 1].date ?? null : null;
  const beyondForecast = !!farOut || (!!eventKey && !!lastKey && eventKey > lastKey);
  const eventDayLabel = startsAt ? dayjs.utc(startsAt).tz(tz).format('ddd, MMM D') : '';

  const headIcon = (() => {
    if (view.mode === 'hourly') return weatherIcon(view.hours[0]?.conditionCode);
    if (view.mode === 'daily') return weatherIcon(view.day.conditionCode);
    if (beyondForecast) return { icon: 'CalendarTime', color: 'blue' };
    return { icon: 'CloudRain', color: 'blue' };
  })();

  const leadCode =
    view.mode === 'hourly' ? view.hours[0]?.conditionCode : view.mode === 'daily' ? view.day.conditionCode : null;

  // Part of day (event's tz) shapes both the emoji and the gradient's second hue.
  const leadHour = (() => {
    if (view.mode === 'hourly' && view.hours[0]?.time) return dayjs(view.hours[0].time).tz(tz).hour();
    if (startsAt) return dayjs.utc(startsAt).tz(tz).hour();
    return 12;
  })();
  const part = partOfDay(leadHour);
  const moodEmoji = moodEmojiFor(leadCode, part);
  // Day → pure condition hue; dawn/dusk/night blend in a warm/indigo tone.
  const timeAccent = part === 'morning' ? 'orange' : part === 'evening' ? 'grape' : part === 'night' ? 'indigo' : headIcon.color;
  const cardGradient = `linear-gradient(135deg, var(--mantine-color-${headIcon.color}-0) 0%, var(--mantine-color-${timeAccent}-1) 100%)`;

  // The forecast day matching the event (for sunrise/sunset).
  // ⚠ The event's day or none — `days[0]` is TODAY, and today's sunset under the event's date is the #182 mistake again.
  const relevantDay: WeatherForecastDay | null = (() => {
    if (view.mode === 'daily') return view.day;
    if (forecast?.days?.length && startsAt) {
      const key = eventDayKey(startsAt, tz);
      return forecast.days.find((d) => d.date === key) ?? null;
    }
    return forecast?.days?.[0] ?? null;
  })();
  const sunset = relevantDay?.sunsetTime ?? null;

  // Air quality: current level + does it worsen across the window?
  const aq = airQuality && airQuality.aqi != null ? airQuality : null;
  const aqRank = aq ? aqiRank(aq.category) : 0;
  const aqWorst = (aq?.hours ?? []).reduce<{ rank: number; time: string | null; category: string | null }>(
    (m, h) => {
      const r = aqiRank(h.category);
      return r > m.rank ? { rank: r, time: h.time ?? null, category: h.category ?? null } : m;
    },
    { rank: aqRank, time: null, category: aq?.category ?? null },
  );
  const aqWorsening = !!aq && aqWorst.rank > aqRank && aqWorst.rank >= 2;

  // Pollen for the event's day — flag the worst elevated type (grass/tree/weed).
  const pollenDay = (() => {
    if (!pollen?.days?.length) return null;
    if (startsAt) {
      const key = eventDayKey(startsAt, tz);
      return pollen.days.find((d) => d.date === key) ?? null;
    }
    return pollen.days[0];
  })();
  const worstPollen = (pollenDay?.types ?? []).reduce<{ index: number; name: string | null; category: string | null }>(
    (m, tp) => ((tp.index ?? 0) > m.index ? { index: tp.index ?? 0, name: tp.displayName ?? null, category: tp.category ?? null } : m),
    { index: 0, name: null, category: null },
  );
  const pollenElevated = worstPollen.index >= POLLEN_ADVISORY;

  // Drop the precip row entirely on a dry stretch rather than leaving every
  // column padded by an empty slot.
  const showPrecipRow = view.mode === 'hourly' && view.hours.some((h) => (h.precipProbabilityPct ?? 0) > 0);

  // Which date/hours the numbers below actually describe. Without this the panel
  // reads as "the weather right now" — the whole point is the event's outlook.
  const dayLabel = (iso?: string | null): string => {
    if (!iso) {
      return '';
    }

    const local = dayjs(iso).tz(tz);
    const diff = local.startOf('day').diff(dayjs().tz(tz).startOf('day'), 'day');

    if (diff === 0) {
      return t('events:dash.weatherToday', { defaultValue: 'Today' });
    }

    if (diff === 1) {
      return t('events:dash.weatherTomorrow', { defaultValue: 'Tomorrow' });
    }

    return local.format('ddd, MMM D');
  };

  const subtitle = (() => {
    if (view.mode === 'daily') {
      return t('events:dash.weatherDayOutlook', {
        day: dayLabel(view.day.date),
        defaultValue: '{{day}} · all-day outlook',
      });
    }

    if (view.mode === 'hourly') {
      if (view.multiDay) {
        return t('events:dash.weatherNext24', { defaultValue: 'Next 24 hours' });
      }

      const day = dayLabel(view.rangeStart);
      const from = hourLabel(view.rangeStart, tz);
      const to = hourLabel(view.rangeEnd, tz);

      if (view.ongoing) {
        return t('events:dash.weatherRestOfEvent', {
          day,
          to,
          defaultValue: '{{day}} · now until {{to}}',
        });
      }

      return `${day} · ${from} – ${to}`;
    }

    return '';
  })();

  return (
    <Card withBorder radius="md" padding="md" h="100%" style={{ position: 'relative', overflow: 'hidden', background: cardGradient }}>
      {/* Mood emoji: big, faint, tucked to the right behind the content. */}
      <Box
        aria-hidden
        style={{
          position: 'absolute',
          right: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 132,
          lineHeight: 1,
          opacity: 0.14,
          filter: 'saturate(1.2)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {moodEmoji}
      </Box>

      <Box style={{ position: 'relative', zIndex: 1 }}>
      <Group justify="space-between" wrap="nowrap" mb="xs">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" color={headIcon.color} size={28} radius="md">
            <AppIcon iconName={headIcon.icon} size={16} />
          </ThemeIcon>
          <Stack gap={0}>
            <Text fw={600} size="md" lh={1.2}>
              {t('events:dash.weather', { defaultValue: 'Weather' })}
            </Text>
            {locationLabel && (
              <Text size="xs" c="dimmed" lh={1.3}>
                {locationLabel}
              </Text>
            )}
            {subtitle && (
              <Text size="xs" c="dimmed" lh={1.3}>
                {subtitle}
              </Text>
            )}
          </Stack>
        </Group>
        {loading && <Loader size="xs" />}
      </Group>

      {view.mode === 'none' && (
        <Text size="md" c="dimmed">
          {loading
            ? t('events:dash.weatherLoading', { defaultValue: 'Checking the forecast…' })
            : beyondForecast
              ? t('events:dash.weatherNotYet', {
                  day: eventDayLabel,
                  count: EVENT_FORECAST_DAYS,
                  defaultValue: 'Your event on {{day}} is further out than the forecast reaches. It will show here about {{count}} days before.',
                })
              : locationLabel
              ? t('events:dash.weatherUnavailableFor', {
                  location: locationLabel,
                  defaultValue: 'Forecast unavailable for {{location}}.',
                })
              : t('events:dash.weatherUnavailable', { defaultValue: 'Forecast unavailable for this location.' })}
        </Text>
      )}

      {view.mode === 'daily' && (
        <Stack gap={6}>
          <Group gap="sm" align="center" wrap="nowrap">
            <AppIcon iconName={headIcon.icon} size={30} color={`var(--mantine-color-${headIcon.color}-6)`} />
            <Text fz={26} fw={700} lh={1} c={tempColorFor(view.day.tempMaxC)}>
              {tempLabel(view.day.tempMaxC)}
              <Text span size="md" c="dimmed">
                {' '}
                / {tempLabel(view.day.tempMinC)}
              </Text>
            </Text>
          </Group>
          <Text size="md" c="dimmed" style={{ textTransform: 'capitalize' }}>
            {view.day.description || view.day.conditionCode || ''}
            {view.day.precipProbabilityPct != null ? ` · ${view.day.precipProbabilityPct}% ${t('events:dash.rainChance', { defaultValue: 'rain' })}` : ''}
          </Text>
        </Stack>
      )}

      {view.mode === 'hourly' && (
        <Stack gap="xs">
          {view.warnings.map((warning, i) => (
            <Group
              key={i}
              gap={6}
              wrap="nowrap"
              px={8}
              py={5}
              style={{
                borderRadius: 'var(--mantine-radius-sm)',
                background: `var(--mantine-color-${warning.color}-1)`,
              }}
            >
              <AppIcon iconName={warning.icon} size={14} color={`var(--mantine-color-${warning.color}-7)`} />
              <Text size="md" lh={1.3} lineClamp={1}>
                {warning.text}
              </Text>
            </Group>
          ))}
          {/* type="hover" keeps the bar hidden until the user hovers, so it
              never sits against the precip numbers; offsetScrollbars reserves a
              gap so it doesn't overlap them when it does appear. The clipped
              last column is the always-on hint that there's more to scroll. */}
          <ScrollArea type="hover" scrollbarSize={6} offsetScrollbars="x" scrollbars="x">
            {/* align="flex-start" + a reserved precip slot: without both, hours
                that have no rain chance render a shorter column and Mantine
                centres it, so the icons and temperatures zig-zag. */}
            <Group gap="md" wrap="nowrap" align="flex-start" style={{ minWidth: 'min-content' }}>
              {view.hours.map((h) => {
                const wi = weatherIcon(h.conditionCode);
                const precip = h.precipProbabilityPct ?? 0;
                const tint = roughHourTint(h);
                return (
                  <Stack
                    key={h.time}
                    gap={4}
                    align="center"
                    style={{ flexShrink: 0, background: tint, borderRadius: tint ? 8 : undefined, padding: tint ? '4px 8px' : undefined }}
                  >
                    <Text size="xs" c="dimmed" lh={1}>
                      {hourLabel(h.time, tz)}
                    </Text>
                    <AppIcon iconName={wi.icon} size={20} color={`var(--mantine-color-${wi.color}-6)`} />
                    <Text size="md" fw={600} lh={1} c={tempColorFor(h.tempC)}>
                      {tempLabel(h.tempC)}
                    </Text>
                    {showPrecipRow && (
                      <Group gap={2} wrap="nowrap" style={{ visibility: precip > 0 ? 'visible' : 'hidden' }}>
                        <AppIcon iconName="Droplet" size={11} />
                        <Text size="md" c="blue.7" lh={1}>
                          {precip}%
                        </Text>
                      </Group>
                    )}
                  </Stack>
                );
              })}
            </Group>
          </ScrollArea>
        </Stack>
      )}

      {/* Air-quality worsening advisory (only when close to the event). */}
      {aqWorsening && (
        <Group
          gap={6}
          wrap="nowrap"
          px={8}
          py={5}
          mt="xs"
          style={{ borderRadius: 'var(--mantine-radius-sm)', background: `var(--mantine-color-${aqiColor(aqWorst.rank)}-1)` }}
        >
          <AppIcon iconName="Wind" size={14} color={`var(--mantine-color-${aqiColor(aqWorst.rank)}-7)`} />
          <Text size="md" lh={1.3} lineClamp={1}>
            {t('events:dash.aqiWorsening', {
              category: aqWorst.category ?? '',
              at: aqWorst.time ? ` around ${hourLabel(aqWorst.time, tz)}` : '',
              defaultValue: 'Air quality worsening to {{category}}{{at}}',
            })}
          </Text>
        </Group>
      )}

      {/* Elevated-pollen advisory (only when close to the event). */}
      {pollenElevated && (
        <Group
          gap={6}
          wrap="nowrap"
          px={8}
          py={5}
          mt="xs"
          style={{ borderRadius: 'var(--mantine-radius-sm)', background: `var(--mantine-color-${pollenColor(worstPollen.index)}-1)` }}
        >
          <AppIcon iconName="Flower" size={14} color={`var(--mantine-color-${pollenColor(worstPollen.index)}-7)`} />
          <Text size="md" lh={1.3} lineClamp={1}>
            {t('events:dash.pollenAdvisory', {
              type: worstPollen.name ?? 'Pollen',
              level: (worstPollen.category ?? '').toLowerCase(),
              defaultValue: '{{type}} pollen {{level}} — heads up for allergy-prone guests',
            })}
          </Text>
        </Group>
      )}

      {/* Footer: current AQI + pollen + sunset (all optional). */}
      {(aq || pollenElevated || sunset) && (
        <Group gap="md" wrap="wrap" mt="sm">
          {aq && (
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              <Badge color={aqiColor(aqRank)} variant="light" size="sm">
                {t('events:dash.aqi', { defaultValue: 'AQI' })} {aq.aqi}
              </Badge>
              {aq.category && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {aq.category}
                </Text>
              )}
            </Group>
          )}
          {pollenElevated && (
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              <Badge color={pollenColor(worstPollen.index)} variant="light" size="sm" leftSection={<AppIcon iconName="Flower" size={11} />}>
                {worstPollen.name} {worstPollen.category}
              </Badge>
            </Group>
          )}
          {sunset && (
            <Text size="xs" c="dimmed">
              🌇 {t('events:dash.sunset', { defaultValue: 'Sunset' })} {timeLabel(sunset, tz)}
            </Text>
          )}
        </Group>
      )}
      </Box>

      {footer && (
        <>
          <Divider my="md" />
          {footer}
        </>
      )}
    </Card>
  );
};
